/**
 * External dependencies
 */
import { LitElement, html, css, unsafeCSS } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { isEmpty } from '@automator/utilities';

/**
 * Internal dependencies
 */
import {
	uapRecipeTemplateLibraryConfig,
	uapRtlUrlFilterQueryManager
} from '../../utilities';

/**
 * Styles
 */
import styles from './web-component-style.scss';

/**
 * Defines <uap-rtl-grid-display> custom element
 */
@customElement( 'uap-rtl-grid-display' )
class UAPRTL_GridDisplay extends LitElement {

	// Define style
	static styles = css`${ unsafeCSS( styles ) }`;

	@property( { type: Object, attribute: false } ) accessor baseResults = null;

	@property( { type: Object, attribute: false } ) accessor filter = {};

	@property( { type: Number, attribute: false } ) accessor paged = 1;

	@property( { type: Object, attribute: false } ) accessor scrollContainer = null;

	@property( { type: Object, attribute: false } ) accessor scrollToPage = 1;

	@property( { type: Boolean, attribute: false } ) accessor showAddNewTemplate = false;

	library = uapRecipeTemplateLibraryConfig;

	isReady = false;

	observer = null;

	/**
	 * Called when the component is first updated
	 * This is used to setup the intersection observer
	 * and populate the paged query variable
	 *
	 * @return {void}
	 */
	firstUpdated() {
		this._setupIntersectionObserver();
		this.scrollContainer = document.querySelector( '#uap-template-library' );
	}

	/**
	 * Called when the component is updated
	 *
	 * @param {Map} changedProperties - The properties that changed
	 *
	 * @return {void}
	 */
	updated( changedProperties ) {
		super.updated( changedProperties );

		// Check if the filter property has changed
		if ( changedProperties.has( 'filter' ) ) {

			// If the base results are not set, set them to all of the library templates IDs.
			if ( this.baseResults === null ) {
				this.baseResults = this.library.templates.map( template => template.id );
			}

			this.showAddNewTemplate = this.filter.type === 'all';

			if ( this.filter.page === 1 ) {
				// Scroll to the top of the container
				this._scrollToTop();
			}

			// Wait for the next animation frame after the update
			this.updateComplete.then( () => {
				this._setupIntersectionObserver();
			} );
		}

		// Check if the scrollToPage property has changed
		if ( changedProperties.has( 'scrollToPage' ) && this.scrollToPage > 1 ) {
			// Wait for the next animation frame after the update
			this.updateComplete.then( () => {
				this._scrollToPage();
			} );
		}
	}

	/**
	 * Initialize the IntersectionObserver
	 * This is used to detect our "pages" of templates
	 * and populates the "paged" query param accordingly
	 *
	 * @return {void}
	 */
	_setupIntersectionObserver() {
		// Disconnect the observer if it exists
		if ( this.observer ) {
			this.observer.disconnect();
		}

		// Set the observer options
		const options = {
			root: null, // use the viewport as the root
			rootMargin: '0px',
			threshold: 1.0
		};

		// Create the observer
		this.observer = new IntersectionObserver( ( entries ) => {
			entries.forEach( entry => {
				// If the entry is intersecting, update the paged query variable
				if ( entry.isIntersecting ) {
					const page = parseInt( entry.target.getAttribute( 'data-page' ) );
					if ( page && page !== this.paged ) {
						this.paged = page;
						uapRtlUrlFilterQueryManager( this.filter.filter, this.filter.type, this.paged );
					}
				}
			} );
		}, options );

		// Observe each template grid item
		const items = this.shadowRoot.querySelectorAll( '.template' );
		items.forEach( item => this.observer.observe( item ) );
	}

	/**
	 * Scroll to a specific page on initial load.
	 *
	 * @return {void}
	 */
	_scrollToPage() {
		if ( this.scrollToPage < 2 ) {
			return;
		}
		const targetItem = this.shadowRoot.querySelector(
			`.template[data-page="${this.scrollToPage}"]`
		);

		if ( targetItem ) {
			// Calculate the position of the targetItem relative to the scrollContainer
			const targetRect = targetItem.getBoundingClientRect();
			const containerRect = this.scrollContainer.getBoundingClientRect();

			// Calculate the scroll position
			const scrollTop = this.scrollContainer.scrollTop + ( targetRect.top - containerRect.top );

			// Scroll the container
			this.scrollContainer.scrollTo( { top: scrollTop, behavior: 'smooth' } );
			return;
		}

		// Scroll to the top and reset the paged query variable.
		this._scrollToTop();
	}

	/**
	 * Scroll to the top of the container.
	 *
	 * @return {void}
	 */
	_scrollToTop() {
		this.scrollContainer.scrollTo( {
			top: 0,
			behavior: 'smooth'
		} );
		// Reset the paged query variable
		this.paged = 1;
	}

	/**
	 * Get the current results.
	 *
	 * @return {Array} The current results
	 */
	_get_current_results() {
		this.isReady = this.library.isReady || false;
		if ( ! this.isReady || isEmpty( this.filter ) ) {
			return [];
		}

		const { results } = this.filter;

		if ( isEmpty( results ) ) {
			this.showAddNewTemplate = true;
			return [];
		}

		// Only include templates that IDs exist in the base results array
		const filteredResults = results.filter( template => this.baseResults.includes( template.id ) );

		// If no results, show the add new template.
		if ( isEmpty( filteredResults ) ) {
			this.showAddNewTemplate = true;
			return [];
		}

		// Only sort if not a search query.
		if ( this.filter.type !== 'search' ) {
			// Sort results by priority.
			filteredResults.sort( ( a, b ) => a.priority - b.priority );
		}

		// Hide the add new template if the filter is not 'all'.
		if ( this.filter.type !== 'all' && this.showAddNewTemplate ) {
			this.showAddNewTemplate = false;
		}

		return filteredResults;
	}

	/**
	 * Render the integration icon.
	 *
	 * @param {Object} integration - The integration object
	 *
	 * @return {TemplateResult} The integration icon
	 */
	_renderIntegrationIcon( integration ) {
		return html`
			<uo-icon
				integration="${integration.code}"
				title="${integration.name}"
				alt="${integration.name}">
			</uo-icon>`;
	}

	/**
	 * Render the component
	 *
	 * @return {TemplateResult} The template result
	 */
	render() {
		const results = this._get_current_results();
		const itemsPerPage = this.library.itemsPerPage;
		return html`
			<div class="templates">
				${ this.showAddNewTemplate ? html`
					<div class="template">
						<div class="template-add-new">
							<div class="template-add-new__icon">
								<uo-icon id="wand-magic-sparkles"></uo-icon>
							</div>
							<div class="template-add-new__heading">
								${ wp.i18n.__( 'Build your own recipe', 'uncanny-automator' ) }
							</div>
							<div class="template-add-new__description">
								${ wp.i18n.__( 'Create powerful workflows to streamline your tasks', 'uncanny-automator' ) }
							</div>
							<div class="template-add-new__action">
								<uo-button
									color="transparent"
									href=${ this.library.addNewTemplate.url }
								>
									${ wp.i18n.__( 'Create a recipe', 'uncanny-automator' ) }
								</uo-button>
							</div>
						</div>
					</div>
				` : '' }

				${results.map( ( template, index ) => {
					const page = Math.floor( index / itemsPerPage ) + 1;
					return html`
						<div class="template" data-page="${page}">
							<uap-library-template-card
								template-id=${ template.id }
							></uap-library-template-card>
						</div>
					`;
				} )}
			</div>
		`;
	}
}
